import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Table, Spinner, Button, Collapse } from "react-bootstrap";
import * as XLSX from 'xlsx';
import "./NotificationReport.css";

// Helper to get national school_type summary counts
function getNationalSchoolTypeSummary(stateData) {
  const codeToName = new Map([
    ["6", "Other State Govt. Managed"],
    ["8", "Other"],
  ]);

  const toTitleCase = (str) => {
    return String(str).split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const summary = {};
  let totalProcessed = 0;
  let totalNotifications = 0;

  stateData.forEach(state => {
    // Track total notifications from state count
    totalNotifications += state.notifications || 0;

    if (Array.isArray(state.schoolTypes)) {
      state.schoolTypes.forEach(st => {
        // Include both string and numeric school types
        const schoolType = st.school_type;

        // Handle null/undefined by mapping to "Other"
        if (schoolType === null || schoolType === undefined || String(schoolType).trim() === '') {
          const key = "Other";
          if (!summary[key]) summary[key] = 0;
          summary[key] += st.count;
          totalProcessed += st.count;
          console.log(`✅ Added ${st.count} notifications from null/empty school_type to Other`);
          return;
        }

        // Filter out string values "undefined" and "null"
        if (String(schoolType).toLowerCase() === 'undefined' || String(schoolType).toLowerCase() === 'null') {
          return;
        }

        // Map numeric codes to names
        const rawCode = String(schoolType).trim();
        const mappedName = codeToName.get(rawCode) || schoolType;
        const key = toTitleCase(String(mappedName));
        if (!summary[key]) summary[key] = 0;
        summary[key] += st.count;
        totalProcessed += st.count;

        console.log(`✅ Added ${st.count} notifications from school_type: ${schoolType} (${typeof schoolType})`);
      });
    }
  });

  // Calculate missing notifications (where school_type is null/empty/missing)
  const missingCount = totalNotifications - totalProcessed;

  if (missingCount > 0) {
    // Normalize to 'Other' key and merge if already present
    summary['Other'] = (summary['Other'] || 0) + missingCount;
    console.log(`⚠️ Added ${missingCount} notifications with missing/empty school_type to 'Other' category`);
  }

  // Merge numeric code keys (e.g. '8') into 'Other' to avoid duplicate buckets
  if (summary['8']) {
    summary['Other'] = (summary['Other'] || 0) + summary['8'];
    console.log(`🔀 Merged ${summary['8']} items from key '8' into 'Other'`);
    delete summary['8'];
  }

  console.log('📊 Final Summary:', summary);
  console.log('📊 Total in Summary:', Object.values(summary).reduce((sum, val) => sum + val, 0));
  console.log('📊 Expected Total:', totalNotifications);
  console.log(`✅ Count Match: ${Object.values(summary).reduce((sum, val) => sum + val, 0) === totalNotifications ? 'YES' : 'NO'}`);

  return summary;
}

// Helper to create ministry-style grouped counts for national data
function getNationalMinistrySummary(stateData) {
  const normalizeName = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\./g, "")
      .replace(/\s+/g, " ");

  const codeToName = new Map([
    ["6", "other state govt managed"],
    ["8", "other"],
  ]);

  const governmentNames = new Set([
    "department of education",
    "tribal welfare department",
    "local body",
    "other state govt managed",
    "minority affairs department",
    "social welfare department",
    "ministry of labour",
    "sainik school",
    "railway school",
    "central tibetan school",
    "other central govt/psu schools",
    "other central govt/psu schools**",
    "kendriya vidyalaya",
    "jawahar navodaya vidyalaya",
    "kvs",
    "central govt",
    "central government",
  ]);

  const aidedNames = new Set([
    "government aided",
    "partially govt aided",
    "partially government aided",
  ]);

  const summary = {
    government: 0,
    aided: 0,
    privateOther: 0,
  };

  stateData.forEach(state => {
    if (Array.isArray(state.schoolTypes)) {
      state.schoolTypes.forEach(st => {
        const rawValue = st.school_type;
        const count = Number(st.count) || 0;
        
        // Handle null/undefined by putting in privateOther
        if (rawValue === null || rawValue === undefined || String(rawValue).trim() === '') {
          summary.privateOther += count;
          return;
        }
        
        const rawCode = String(rawValue).trim();
        const mappedName = codeToName.get(rawCode);
        const normalizedName = mappedName || normalizeName(rawValue);
        if (normalizedName === "undefined" || normalizedName === "null") {
          summary.privateOther += count;
          return;
        }

        if (rawCode === "98") {
          summary.privateOther += count;
        } else if (governmentNames.has(normalizedName)) {
          summary.government += count;
        } else if (aidedNames.has(normalizedName)) {
          summary.aided += count;
        } else {
          summary.privateOther += count;
        }
      });
    }
  });

  return summary;
}

// Helper to get ministry summary for a single state
function getStateMinistrySummary(schoolTypes) {
  const normalizeName = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\./g, "")
      .replace(/\s+/g, " ");

  const codeToName = new Map([
    ["6", "other state govt managed"],
    ["8", "other"],
  ]);

  const governmentNames = new Set([
    "department of education",
    "tribal welfare department",
    "local body",
    "other state govt managed",
    "minority affairs department",
    "social welfare department",
    "ministry of labour",
    "sainik school",
    "railway school",
    "central tibetan school",
    "other central govt/psu schools",
    "other central govt/psu schools**",
    "kendriya vidyalaya",
    "jawahar navodaya vidyalaya",
    "kvs",
    "central govt",
    "central government",
  ]);

  const aidedNames = new Set([
    "government aided",
    "partially govt aided",
    "partially government aided"
  ]);

  const summary = {
    government: 0,
    aided: 0,
    privateOther: 0,
  };

  if (Array.isArray(schoolTypes)) {
    schoolTypes.forEach(st => {
      const rawValue = st.school_type;
      const count = Number(st.count) || 0;
      
      // Handle null/undefined by putting in privateOther
      if (rawValue === null || rawValue === undefined || String(rawValue).trim() === '') {
        summary.privateOther += count;
        return;
      }
      
      const rawCode = String(rawValue).trim();
      const mappedName = codeToName.get(rawCode);
      const normalizedName = mappedName || normalizeName(rawValue);
      if (normalizedName === "undefined" || normalizedName === "null") {
        summary.privateOther += count;
        return;
      }

      if (rawCode === "98") {
        summary.privateOther += count;
      } else if (governmentNames.has(normalizedName)) {
        summary.government += count;
      } else if (aidedNames.has(normalizedName)) {
        summary.aided += count;
      } else {
        summary.privateOther += count;
      }
    });
  }

  return summary;
}

// `embedded` renders this inside the admin report shell (no Back button,
// and a state click stays in the shell via onOpenState instead of routing).
function NotificationReport({ embedded = false, onOpenState = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const passedData = location.state;

  const [loading, setLoading] = useState(!passedData);
  const [totalNotifications, setTotalNotifications] = useState(passedData?.totalCount || 0);
  const [stateData, setStateData] = useState(passedData?.stateData || []);
  const [schoolTypeSummary, setSchoolTypeSummary] = useState({});
  const [ministrySummary, setMinistrySummary] = useState({ government: 0, aided: 0, privateOther: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [isMinistryOpen, setIsMinistryOpen] = useState(false);
  const [expandedStates, setExpandedStates] = useState({});

  useEffect(() => {
    // Always fetch fresh data
    fetchNotificationReport();
  }, []);

  const fetchNotificationReport = async () => {
    try {
      const response = await fetch('https://ecoclubs.education.gov.in/api/school-notifications/all-states');
      const result = await response.json();

      console.log('🔍 API Response:', result);

      if (result.status === 'success') {
        // Transform and merge duplicates (e.g. "DELHI" and "Delhi")
        const mergedMap = {};
        result.stateCounts.forEach(item => {
          const stateName = String(item.state).trim().toUpperCase();
          if (!mergedMap[stateName]) {
            mergedMap[stateName] = {
              stateName: stateName,
              notifications: 0,
              schoolTypes: []
            };
          }
          mergedMap[stateName].notifications += item.count;
          if (Array.isArray(item.schoolTypes)) {
            mergedMap[stateName].schoolTypes.push(...item.schoolTypes);
          }
        });

        const transformedData = Object.values(mergedMap).sort((a, b) => 
          a.stateName.localeCompare(b.stateName)
        );

        console.log('📊 Transformed Data:', transformedData);
        const summary = getNationalSchoolTypeSummary(transformedData);
        const ministrySummary = getNationalMinistrySummary(transformedData);
        console.log('📈 School Type Summary:', summary);
        console.log('📈 Ministry Summary:', ministrySummary);

        setStateData(transformedData);
        setTotalNotifications(result.totalCount);
        setSchoolTypeSummary(summary);
        setMinistrySummary(ministrySummary);
        
        // Expand all states by default
        const allExpanded = {};
        transformedData.forEach((_, index) => {
          allExpanded[index] = true;
        });
        setExpandedStates(allExpanded);
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error fetching notification report:', error.message, error.stack);
      } else {
        console.error('Error fetching notification report:', JSON.stringify(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStateClick = async (stateName) => {
    const goState = (data) =>
      embedded && onOpenState
        ? onOpenState(stateName, data || null)
        : navigate(`/district-report/${encodeURIComponent(stateName)}`, data ? { state: data } : undefined);
    // Fetch both district data and school type data before navigation
    try {
      const [districtResponse, schoolTypeResponse] = await Promise.all([
        fetch('https://ecoclubs.education.gov.in/api/school-notifications/districts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ state: stateName })
        }),
        fetch('https://ecoclubs.education.gov.in/api/school-notifications/school-types', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ state: stateName })
        })
      ]);

      const districtResult = await districtResponse.json();
      const schoolTypeResult = await schoolTypeResponse.json();

      if (districtResult.status === 'success' && schoolTypeResult.status === 'success') {
        // Navigate with pre-fetched data
        goState({
          districtCounts: districtResult.districtCounts,
          totalCount: districtResult.totalCount,
          schoolTypeCounts: schoolTypeResult.schoolTypeCounts
        });
      } else {
        goState();
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error fetching district/school type data:', error.message, error.stack);
      } else {
        console.error('Error fetching district/school type data:', JSON.stringify(error));
      }
      goState();
    }
  };

  const handleBackClick = () => {
    // Go back where the visitor actually came from (the admin reports
    // dashboard, usually) rather than always dumping them on the landing page.
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const toggleState = (index) => {
    setExpandedStates(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const downloadMinistryReport = async () => {
    try {
      const reportData = await Promise.all(
        stateData.map(async (state, index) => {
          try {
            // Fetch complete district data for accurate ministry summary
            const response = await fetch('https://ecoclubs.education.gov.in/api/school-notifications/districts', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ state: state.stateName })
            });
            const result = await response.json();
            
            let stateMinistrySummary = { government: 0, aided: 0, privateOther: 0 };
            if (result.status === 'success' && result.districtCounts) {
              const allSchoolTypes = [];
              result.districtCounts.forEach(district => {
                if (Array.isArray(district.schoolTypes)) {
                  allSchoolTypes.push(...district.schoolTypes);
                }
              });
              stateMinistrySummary = getStateMinistrySummary(allSchoolTypes);
              
              // Special case for Kendriya and Navodaya Vidyalaya - all should be Government
              if (state.stateName.toUpperCase().includes('KENDRIYA') || state.stateName.toUpperCase().includes('NAVODAYA')) {
                const totalInSummary = stateMinistrySummary.government + stateMinistrySummary.aided + stateMinistrySummary.privateOther;
                stateMinistrySummary.government = totalInSummary || state.notifications;
                stateMinistrySummary.aided = 0;
                stateMinistrySummary.privateOther = 0;
              }
            }
            
            // Determine total uploads for the state from district API (prefer result.totalCount)
            let stateTotalUploads = 0;
            if (result.status === 'success') {
              // Prefer explicit totalCount if provided
              stateTotalUploads = Number(result.totalCount) || 0;
              // Fallback: sum district.total if totalCount isn't available
              if (!stateTotalUploads && Array.isArray(result.districtCounts)) {
                stateTotalUploads = result.districtCounts.reduce((s, d) => s + (Number(d.total) || 0), 0);
              }
            } else {
              stateTotalUploads = Number(state.notifications) || 0;
            }

            return {
              'S.No': index + 1,
              'States/UTs/ABs': state.stateName,
              'Government Schools': stateMinistrySummary.government,
              'Government Aided Schools': stateMinistrySummary.aided,
              'Private and Other Schools': stateMinistrySummary.privateOther,
              'Total Notifications (Summary)': state.notifications,
              'Total Uploads': stateTotalUploads
            };
          } catch (error) {
            console.error(`Error fetching data for ${state.stateName}:`, error);
            return {
              'S.No': index + 1,
              'States/UTs/ABs': state.stateName,
              'Government Schools': 0,
              'Government Aided Schools': 0,
              'Private and Other Schools': 0,
              'Total Notifications (Summary)': state.notifications,
              'Total Uploads': state.notifications
            };
          }
        })
      );

      // Compute totals for uploads and summary notifications
      const totalUploadsSum = reportData.reduce((s, r) => s + (Number(r['Total Uploads']) || 0), 0);
      const totalSummaryNotifications = reportData.reduce((s, r) => s + (Number(r['Total Notifications (Summary)']) || 0), 0);

      // Add totals row
      reportData.push({
        'S.No': '',
        'States/UTs/ABs': 'TOTAL',
        'Government Schools': ministrySummary.government,
        'Government Aided Schools': ministrySummary.aided,
        'Private and Other Schools': ministrySummary.privateOther,
        'Total Notifications (Summary)': totalSummaryNotifications,
        'Total Uploads': totalUploadsSum
      });

      const worksheet = XLSX.utils.json_to_sheet(reportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ministry Report');
      
      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const filename = `Ministry_Report_State_Wise_${date}.xlsx`;
      
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error downloading ministry report:', error);
      alert('Error generating download. Please try again.');
    }
  };

  // Component to fetch and display state ministry summary with complete district data
  const StateMinistryCard = ({ stateName }) => {
    const [ministrySummary, setMinistrySummary] = useState(null);

    useEffect(() => {
      const fetchDistrictData = async () => {
        try {
          const response = await fetch('https://ecoclubs.education.gov.in/api/school-notifications/districts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ state: stateName })
          });
          const result = await response.json();
          if (result.status === 'success' && result.districtCounts) {
            // Combine all school types from all districts
            const allSchoolTypes = [];
            result.districtCounts.forEach(district => {
              if (Array.isArray(district.schoolTypes)) {
                allSchoolTypes.push(...district.schoolTypes);
              }
            });
            const summary = getStateMinistrySummary(allSchoolTypes);
            
            // Special case for Kendriya and Navodaya Vidyalaya
            if (stateName.toUpperCase().includes('KENDRIYA') || stateName.toUpperCase().includes('NAVODAYA')) {
              const total = summary.government + summary.aided + summary.privateOther;
              setMinistrySummary({
                government: total,
                aided: 0,
                privateOther: 0
              });
            } else {
              setMinistrySummary(summary);
            }
          }
        } catch (error) {
          console.error('Error fetching district data for ministry summary:', error);
        }
      };

      fetchDistrictData();
    }, [stateName]);

    if (!ministrySummary) {
      return <div className="text-center">Loading...</div>;
    }

    return (
      <div className="district-cards-grid">
        <div className="district-card">
          <div className="district-card-name">GOVERNMENT SCHOOLS</div>
          <div className="district-card-count">{ministrySummary.government.toLocaleString("en-IN")}</div>
        </div>
        <div className="district-card">
          <div className="district-card-name">GOVERNMENT AIDED SCHOOLS</div>
          <div className="district-card-count">{ministrySummary.aided.toLocaleString("en-IN")}</div>
        </div>
        <div className="district-card">
          <div className="district-card-name">PRIVATE AND OTHER SCHOOLS</div>
          <div className="district-card-count">{ministrySummary.privateOther.toLocaleString("en-IN")}</div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="notification-report-container">
        <div className="container-fluid">
          <div className="report-header">
            {!embedded && (
              <Button
                variant="success"
                className="back-button-custom"
                onClick={handleBackClick}
              >
                ← Back
              </Button>
            )}
            <h1 className="report-title">States/UTs/ABs-wise Notifications Report</h1>
            <Button
              variant="primary"
              className="back-button-custom"
              onClick={downloadMinistryReport}
              style={{ marginLeft: 'auto' }}
            >
              📥 Download  Report
            </Button>
          </div>

          <div className="total-notifications-card">
            <h3>TOTAL NOTIFICATIONS</h3>
            <h2 className="total-count">{totalNotifications.toLocaleString("en-IN")}</h2>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="mt-3">Loading data...</p>
            </div>
          ) : (
            <>
              {/* Debug Info */}
              {console.log('🎯 Rendering - School Type Summary:', schoolTypeSummary)}
              {console.log('🎯 Summary Keys Length:', Object.keys(schoolTypeSummary).length)}

             

              {/* National School Type Summary Accordion */}
              {Object.keys(schoolTypeSummary).length > 0 ? (
                <div className="school-type-section">
                  <button
                    className="accordion-toggle"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                  >
                    <span className="accordion-title">
                      National School Type Categories ({Object.keys(schoolTypeSummary).length})
                    </span>
                    <span className={`accordion-arrow ${isOpen ? 'open' : ''}`}>
                      
                    </span>
                  </button>

                  <Collapse in={isOpen}>
                    <div className="district-cards-grid">
                      {Object.entries(schoolTypeSummary).map(([type, count]) => (
                        <div key={type} className="district-card">
                          <div className="district-card-name">{type.toUpperCase()}</div>
                          <div className="district-card-count">{count.toLocaleString("en-IN")}</div>
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', background: 'white', margin: '20px', borderRadius: '10px' }}>
                  <p style={{ color: '#666' }}>No school type data available</p>
                </div>
              )}

              {/* State Table */}
              <div className="report-table-container">
                <Table responsive bordered hover className="notification-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>States/UTs/ABs</th>
                      <th>NOTIFICATIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stateData.map((state, index) => (
                      <React.Fragment key={index}>
                        <tr 
                          onClick={() => toggleState(index)}
                          style={{ cursor: 'pointer' }}
                          className="district-row"
                        >
                          <td>{index + 1}</td>
                          <td className="district-name">
                            
                            <span
                              className="state-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStateClick(state.stateName);
                              }}
                            >
                              {state.stateName}
                            </span>
                          </td>
                          <td className="notification-count">
                            <strong>{state.notifications.toLocaleString("en-IN")}</strong>
                          </td>
                        </tr>
                       
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default NotificationReport;